import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';

const RepairManagementPage = () => {
  const navigate = useNavigate();
  const { approveRepair, rejectRepair, approveRepairCompletion, markRepairAsCompleted, approveReceiveRepair, rejectReceiveRepair } = useEquipment();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState('pending'); // 'pending' or 'history'
  const [pendingFilterTab, setPendingFilterTab] = useState('pending'); // pending, approved, in_progress, all
  const [historyFilterTab, setHistoryFilterTab] = useState('completed'); // completed, rejected, all
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showRejectReceiveModal, setShowRejectReceiveModal] = useState(false);
  const [rejectingReceiveId, setRejectingReceiveId] = useState(null);
  const [receiveRejectionReason, setReceiveRejectionReason] = useState('');

  useEffect(() => {
    const loadRepairs = async () => {
      if (!currentTeam) {
        setLoading(false);
        return;
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        alert('คุณไม่มีสิทธิ์เข้าถึงหน้านี้');
        navigate('/nurse/equipment');
        return;
      }

      try {
        setLoading(true);
        const repairsQuery = query(
          collection(db, 'equipmentRepairs'),
          where('teamId', '==', currentTeam.id),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(repairsQuery);
        const repairsData = await Promise.all(
          snapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();
            // Get equipment details
            const equipmentRef = doc(db, 'equipment', data.equipmentId);
            const equipmentSnap = await getDoc(equipmentRef);
            const equipmentData = equipmentSnap.exists() ? equipmentSnap.data() : null;

            // Get reporter details
            const reporterRef = doc(db, 'users', data.reportedBy);
            const reporterSnap = await getDoc(reporterRef);
            const reporterData = reporterSnap.exists() ? reporterSnap.data() : null;

            return {
              id: docSnapshot.id,
              ...data,
              equipment: equipmentData,
              reporter: reporterData
            };
          })
        );

        setRepairs(repairsData);
      } catch (err) {
        console.error('Error loading repairs:', err);
        // Fallback: query without orderBy
        try {
          const fallbackQuery = query(
            collection(db, 'equipmentRepairs'),
            where('teamId', '==', currentTeam.id)
          );
          const snapshot = await getDocs(fallbackQuery);
          const repairsData = await Promise.all(
            snapshot.docs.map(async (docSnapshot) => {
              const data = docSnapshot.data();
              const equipmentRef = doc(db, 'equipment', data.equipmentId);
              const equipmentSnap = await getDoc(equipmentRef);
              const equipmentData = equipmentSnap.exists() ? equipmentSnap.data() : null;
              const reporterRef = doc(db, 'users', data.reportedBy);
              const reporterSnap = await getDoc(reporterRef);
              const reporterData = reporterSnap.exists() ? reporterSnap.data() : null;

              return {
                id: docSnapshot.id,
                ...data,
                equipment: equipmentData,
                reporter: reporterData
              };
            })
          );
          // Sort in memory
          repairsData.sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
            return dateB - dateA;
          });
          setRepairs(repairsData);
        } catch (fallbackErr) {
          console.error('Error loading repairs (fallback):', fallbackErr);
        }
      } finally {
        setLoading(false);
      }
    };

    loadRepairs();
  }, [currentTeam, navigate]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleApprove = async (repairId) => {
    if (!window.confirm('ยืนยันการอนุมัติคำขอส่งซ่อมนี้หรือไม่?')) return;

    try {
      setIsProcessing(true);
      const result = await approveRepair(repairId);
      if (result.success) {
        alert('อนุมัติสำเร็จ');
        // Reload repairs
        window.location.reload();
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error approving repair:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectingId) return;
    if (!rejectionReason.trim()) {
      alert('กรุณาระบุเหตุผลในการปฏิเสธ');
      return;
    }

    try {
      setIsProcessing(true);
      const result = await rejectRepair(rejectingId, rejectionReason.trim());
      if (result.success) {
        alert('ปฏิเสธสำเร็จ');
        setShowRejectModal(false);
        setRejectingId(null);
        setRejectionReason('');
        // Reload repairs
        window.location.reload();
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error rejecting repair:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApproveReceive = async (repairId) => {
    if (!window.confirm('ยืนยันการอนุมัติการรับกลับอุปกรณ์นี้หรือไม่?')) return;

    try {
      setIsProcessing(true);
      const result = await approveReceiveRepair(repairId);
      if (result.success) {
        alert('อนุมัติการรับกลับสำเร็จ');
        window.location.reload();
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error approving receive:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectReceive = async () => {
    if (!rejectingReceiveId) return;
    if (!receiveRejectionReason.trim()) {
      alert('กรุณาระบุเหตุผลในการปฏิเสธ');
      return;
    }

    try {
      setIsProcessing(true);
      const result = await rejectReceiveRepair(rejectingReceiveId, receiveRejectionReason.trim());
      if (result.success) {
        alert('ปฏิเสธสำเร็จ');
        setShowRejectReceiveModal(false);
        setRejectingReceiveId(null);
        setReceiveRejectionReason('');
        window.location.reload();
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error rejecting receive:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApproveCompletion = async (repairId) => {
    if (!window.confirm('ยืนยันการอนุมัติคำขอซ่อมเสร็จนี้หรือไม่?')) return;

    try {
      setIsProcessing(true);
      const result = await approveRepairCompletion(repairId);
      if (result.success) {
        alert('อนุมัติคำขอซ่อมเสร็จสำเร็จ');
        window.location.reload();
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error approving completion:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMarkAsCompleted = async (repairId) => {
    if (!window.confirm('ยืนยันว่าอุปกรณ์ซ่อมเสร็จแล้วหรือไม่?')) return;

    try {
      setIsProcessing(true);
      const result = await markRepairAsCompleted(repairId);
      if (result.success) {
        alert('อัปเดตสถานะเป็น "ซ่อมเสร็จแล้ว" สำเร็จ');
        window.location.reload();
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error marking repair as completed:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Calculate statistics
  const pendingStats = {
    pending: repairs.filter(r => r.status === 'PENDING').length,
    approved: repairs.filter(r => r.status === 'APPROVED').length,
    inProgress: repairs.filter(r => r.status === 'IN_PROGRESS').length,
    total: repairs.filter(r => r.status === 'PENDING' || r.status === 'APPROVED' || r.status === 'IN_PROGRESS').length
  };

  const historyStats = {
    completed: repairs.filter(r => r.status === 'COMPLETED').length,
    rejected: repairs.filter(r => r.status === 'REJECTED').length,
    total: repairs.filter(r => r.status === 'COMPLETED' || r.status === 'REJECTED').length
  };

  // Filter repairs based on mainTab and filterTab
  const filteredRepairs = repairs.filter(repair => {
    if (mainTab === 'pending') {
      // Show pending/approved/in_progress and completed with pending receive
      if (repair.status === 'COMPLETED' && repair.receiveStatus === 'PENDING') {
        if (pendingFilterTab === 'all' || pendingFilterTab === 'receive_pending') return true;
        return false;
      }
      if (repair.status !== 'PENDING' && repair.status !== 'APPROVED' && repair.status !== 'IN_PROGRESS') {
        return false;
      }
      if (pendingFilterTab === 'all') return true;
      if (pendingFilterTab === 'pending') return repair.status === 'PENDING';
      if (pendingFilterTab === 'approved') return repair.status === 'APPROVED';
      if (pendingFilterTab === 'in_progress') return repair.status === 'IN_PROGRESS';
      if (pendingFilterTab === 'receive_pending') return repair.status === 'COMPLETED' && repair.receiveStatus === 'PENDING';
      return true;
    } else {
      // Show only completed/rejected
      if (repair.status === 'COMPLETED' && repair.receiveStatus === 'REJECTED') {
        if (historyFilterTab === 'all' || historyFilterTab === 'receive_rejected') return true;
        return false;
      }
      if (repair.status !== 'COMPLETED' && repair.status !== 'REJECTED') {
        return false;
      }
      if (repair.status === 'COMPLETED' && repair.receiveStatus === 'PENDING') {
        return false; // Don't show in history if pending receive
      }
      if (historyFilterTab === 'all') return true;
      if (historyFilterTab === 'completed') return repair.status === 'COMPLETED' && (!repair.receiveStatus || repair.receiveStatus === 'APPROVED');
      if (historyFilterTab === 'rejected') return repair.status === 'REJECTED';
      if (historyFilterTab === 'receive_rejected') return repair.status === 'COMPLETED' && repair.receiveStatus === 'REJECTED';
      return true;
    }
  });

  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <Link
            to="/nurse/teams"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ไปเลือกทีม
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/nurse/equipment"
              className="text-gray-600 hover:text-gray-800"
            >
              ← กลับ
            </Link>
            <h1 className="text-2xl font-bold text-gray-800">จัดการการส่งซ่อม</h1>
          </div>
        </div>

        {/* Statistics */}
        {mainTab === 'pending' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-md p-4">
              <p className="text-sm text-gray-600 mb-1">รออนุมัติ</p>
              <p className="text-2xl font-bold text-yellow-600">{pendingStats.pending}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <p className="text-sm text-gray-600 mb-1">อนุมัติแล้ว</p>
              <p className="text-2xl font-bold text-green-600">{pendingStats.approved}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <p className="text-sm text-gray-600 mb-1">กำลังซ่อม</p>
              <p className="text-2xl font-bold text-blue-600">{pendingStats.inProgress}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-md p-4">
              <p className="text-sm text-gray-600 mb-1">ซ่อมเสร็จ</p>
              <p className="text-2xl font-bold text-gray-600">{historyStats.completed}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <p className="text-sm text-gray-600 mb-1">ปฏิเสธ</p>
              <p className="text-2xl font-bold text-red-600">{historyStats.rejected}</p>
            </div>
          </div>
        )}

        {/* Main Tabs: Pending / History */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex gap-2 border-b border-gray-200 mb-4">
            <button
              onClick={() => setMainTab('pending')}
              className={`px-6 py-3 font-semibold text-lg border-b-2 transition ${
                mainTab === 'pending'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              🔧 รออนุมัติ ({pendingStats.total})
            </button>
            <button
              onClick={() => setMainTab('history')}
              className={`px-6 py-3 font-semibold text-lg border-b-2 transition ${
                mainTab === 'history'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📋 ประวัติ ({historyStats.total})
            </button>
          </div>

          {/* Filter Tabs - Pending */}
          {mainTab === 'pending' && (
            <div className="flex gap-2 overflow-x-auto">
              <button
                onClick={() => setPendingFilterTab('pending')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  pendingFilterTab === 'pending'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                รออนุมัติ ({pendingStats.pending})
              </button>
              <button
                onClick={() => setPendingFilterTab('approved')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  pendingFilterTab === 'approved'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                อนุมัติแล้ว ({pendingStats.approved})
              </button>
              <button
                onClick={() => setPendingFilterTab('in_progress')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  pendingFilterTab === 'in_progress'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                กำลังซ่อม ({pendingStats.inProgress})
              </button>
              <button
                onClick={() => setPendingFilterTab('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  pendingFilterTab === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ทั้งหมด ({pendingStats.total})
              </button>
            </div>
          )}

          {/* Filter Tabs - History */}
          {mainTab === 'history' && (
            <div className="flex gap-2 overflow-x-auto">
              <button
                onClick={() => setHistoryFilterTab('completed')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  historyFilterTab === 'completed'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ซ่อมเสร็จ ({historyStats.completed})
              </button>
              <button
                onClick={() => setHistoryFilterTab('rejected')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  historyFilterTab === 'rejected'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ปฏิเสธ ({historyStats.rejected})
              </button>
              <button
                onClick={() => setHistoryFilterTab('receive_rejected')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  historyFilterTab === 'receive_rejected'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                รับกลับถูกปฏิเสธ ({historyStats.receiveRejected})
              </button>
              <button
                onClick={() => setHistoryFilterTab('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  historyFilterTab === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ทั้งหมด ({historyStats.total})
              </button>
            </div>
          )}
        </div>

        {/* Repairs List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลด...</p>
          </div>
        ) : filteredRepairs.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <div className="text-6xl mb-4">🔧</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">ไม่มีรายการ</h3>
            <p className="text-gray-600">ไม่มีรายการในหมวดหมู่นี้</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRepairs.map(repair => {
              return (
                <div
                  key={repair.id}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {repair.equipment && (
                          <>
                            <span className="text-2xl">🩺</span>
                            <div>
                              <h3 className="font-semibold text-gray-800">{repair.equipment.name || repair.equipmentName}</h3>
                              <p className="text-sm text-gray-600">{repair.equipment.code || repair.equipmentCode}</p>
                            </div>
                          </>
                        )}
                        <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${
                          repair.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          repair.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                          repair.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                          repair.status === 'COMPLETED' && repair.receiveStatus === 'PENDING' ? 'bg-purple-100 text-purple-800' :
                          repair.status === 'COMPLETED' && repair.receiveStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
                          repair.status === 'COMPLETED' && repair.receiveStatus === 'REJECTED' ? 'bg-orange-100 text-orange-800' :
                          repair.status === 'COMPLETED' ? 'bg-gray-100 text-gray-800' :
                          repair.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {repair.status === 'PENDING' ? '⏳ รออนุมัติ' :
                           repair.status === 'APPROVED' ? '🔧 อยู่ระหว่างส่งซ่อม' :
                           repair.status === 'IN_PROGRESS' ? '🔧 กำลังซ่อม' :
                           repair.status === 'COMPLETED' && repair.receiveStatus === 'PENDING' ? '📥 รออนุมัติรับกลับ' :
                           repair.status === 'COMPLETED' && repair.receiveStatus === 'APPROVED' ? '✅ รับกลับแล้ว' :
                           repair.status === 'COMPLETED' && repair.receiveStatus === 'REJECTED' ? '❌ รับกลับถูกปฏิเสธ' :
                           repair.status === 'COMPLETED' ? '✅ ซ่อมเสร็จแล้ว' :
                           repair.status === 'REJECTED' ? '❌ ถูกปฏิเสธ' : repair.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-gray-500">👤 ผู้รายงาน</p>
                          <p className="text-sm font-medium text-gray-800">
                            {repair.reportedByName || repair.reporter?.displayName || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">📅 ส่งคำขอ</p>
                          <p className="text-sm font-medium text-gray-800">
                            {formatDateTime(repair.createdAt)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">⚡ ความเร่งด่วน</p>
                          <p className={`text-sm font-medium ${
                            repair.urgency === 'URGENT' ? 'text-red-600' : 'text-gray-800'
                          }`}>
                            {repair.urgency === 'URGENT' ? '🔴 เร่งด่วน' : '🟢 ปกติ'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">📝 ประเภทปัญหา</p>
                          <p className="text-sm font-medium text-gray-800">
                            {repair.problemType === 'NOT_WORKING' ? 'ไม่ทำงาน' :
                             repair.problemType === 'DAMAGED' ? 'เสียหาย' :
                             repair.problemType === 'MAINTENANCE' ? 'บำรุงรักษา' : 'อื่นๆ'}
                          </p>
                        </div>
                        {repair.approvedAt && (
                          <div>
                            <p className="text-xs text-gray-500">✅ อนุมัติเมื่อ</p>
                            <p className="text-sm font-medium text-gray-800">
                              {formatDateTime(repair.approvedAt)}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              โดย: {repair.approvedByName || '-'}
                            </p>
                          </div>
                        )}
                        {repair.rejectedAt && (
                          <div>
                            <p className="text-xs text-gray-500">❌ ปฏิเสธเมื่อ</p>
                            <p className="text-sm font-medium text-gray-800">
                              {formatDateTime(repair.rejectedAt)}
                            </p>
                            {repair.rejectionReason && (
                              <p className="text-xs text-red-600 mt-1">
                                เหตุผล: {repair.rejectionReason}
                              </p>
                            )}
                          </div>
                        )}
                        {repair.completedAt && (
                          <div>
                            <p className="text-xs text-gray-500">✅ ซ่อมเสร็จเมื่อ</p>
                            <p className="text-sm font-medium text-gray-800">
                              {formatDateTime(repair.completedAt)}
                            </p>
                          </div>
                        )}
                      </div>

                      {repair.problemDescription && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">รายละเอียดปัญหา:</p>
                          <p className="text-sm text-gray-700">{repair.problemDescription}</p>
                        </div>
                      )}

                      {repair.images && repair.images.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs text-gray-500 mb-2">รูปภาพปัญหา:</p>
                          <div className="grid grid-cols-3 gap-2">
                            {repair.images.map((img, idx) => (
                              <img
                                key={idx}
                                src={img}
                                alt={`Repair ${idx + 1}`}
                                className="w-full h-24 object-cover rounded-lg"
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {repair.receiveImages && repair.receiveImages.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs text-gray-500 mb-2">รูปภาพการรับกลับ:</p>
                          <div className="grid grid-cols-3 gap-2">
                            {repair.receiveImages.map((img, idx) => (
                              <img
                                key={idx}
                                src={img}
                                alt={`Receive ${idx + 1}`}
                                className="w-full h-24 object-cover rounded-lg"
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="ml-4 flex flex-col gap-2">
                      {repair.status === 'PENDING' && (
                        <>
                          <button
                            onClick={() => handleApprove(repair.id)}
                            disabled={isProcessing}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                          >
                            ✅ อนุมัติ
                          </button>
                          <button
                            onClick={() => {
                              setRejectingId(repair.id);
                              setShowRejectModal(true);
                            }}
                            disabled={isProcessing}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                          >
                            ❌ ปฏิเสธ
                          </button>
                        </>
                      )}
                      {repair.completionRequestStatus === 'PENDING' && (
                        <button
                          onClick={() => handleApproveCompletion(repair.id)}
                          disabled={isProcessing}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                        >
                          ✅ อนุมัติซ่อมเสร็จ
                        </button>
                      )}
                      {(repair.status === 'APPROVED' || repair.status === 'IN_PROGRESS') && !repair.completionRequestStatus && (
                        <button
                          onClick={() => handleMarkAsCompleted(repair.id)}
                          disabled={isProcessing}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                        >
                          ✅ ซ่อมเสร็จแล้ว
                        </button>
                      )}
                      {repair.status === 'COMPLETED' && repair.receiveStatus === 'PENDING' && (
                        <>
                          <button
                            onClick={() => handleApproveReceive(repair.id)}
                            disabled={isProcessing}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                          >
                            ✅ อนุมัติรับกลับ
                          </button>
                          <button
                            onClick={() => {
                              setRejectingReceiveId(repair.id);
                              setShowRejectReceiveModal(true);
                            }}
                            disabled={isProcessing}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                          >
                            ❌ ปฏิเสธรับกลับ
                          </button>
                        </>
                      )}
                      {repair.equipment && (
                        <Link
                          to={`/nurse/equipment/${repair.equipmentId}`}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                        >
                          ดูรายละเอียด
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">ปฏิเสธคำขอส่งซ่อม</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                เหตุผลที่ปฏิเสธ <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                placeholder="กรุณาระบุเหตุผล..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectingId(null);
                  setRejectionReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleReject}
                disabled={isProcessing || !rejectionReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isProcessing ? 'กำลังส่ง...' : 'ยืนยัน'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RepairManagementPage;
